/* eslint-disable react/jsx-wrap-multilines */
/* eslint-disable camelcase */
import React, { useRef, useState, useEffect } from 'react';
import { useMount, useUnmount } from 'react-use';

import { styled } from '@mui/material/styles';
import { Button, Grid, IconButton, Stack, Typography } from '@mui/material';
import CallIcon from '@mui/icons-material/Call';
import CallEndIcon from '@mui/icons-material/CallEnd';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import { green, red } from '@mui/material/colors';
import Janus from './utils/janus';
import { publishToRoom, calling, hangUp } from './utils/publisher';
import JanusPlayer from './JanusPlayer';
import { callPibo, simulatePibo } from '../../pibo';

const GreenButton = styled(IconButton)(({ theme }) => ({
  width: '40px',
  height: '40px',
  color: theme.palette.getContrastText(green[500]),
  backgroundColor: green[500],
  '&:hover': {
    backgroundColor: green[700],
  },
}));
const RedButton = styled(IconButton)(({ theme }) => ({
  width: '40px',
  height: '40px',
  color: theme.palette.getContrastText(red[500]),
  backgroundColor: red[500],
  '&:hover': {
    backgroundColor: red[700],
  },
}));

const JanusPublisher = ({ janus, opaqueId, caller, callee, children }) => {
  const [playerState, setPlayerState] = useState(null);
  const [remotePlayerState, setRemotePlayerState] = useState(null);
  const [isMuted, setIsMuted] = useState(false);
  const [sfutest, setSfuTest] = useState(null);

  const videoArea = useRef(null);
  const remoteVideoArea = useRef(null);
  let mystream = null;
  let remotestream = null;

  const onStartClick = () => {
    setRemotePlayerState(null);
    calling(sfutest, callee, true);
  };

  const onStopClick = () => {
    setPlayerState('End');
    setRemotePlayerState('End');
    hangUp(sfutest);
    janus.destroy();
  };

  const onMuteClick = () => {
    if (!sfutest.isAudioMuted()) {
      sfutest.muteAudio();
    }
    setIsMuted(sfutest.isAudioMuted());
  };

  const onUnMuteClick = () => {
    if (sfutest.isAudioMuted()) {
      sfutest.unmuteAudio();
    }
    setIsMuted(sfutest.isAudioMuted());
  };

  const TILT = { min: -20, max: 20 };
  const PAN = { min: -40, max: 40 };
  const [{ tilt, pan }, setCam] = useState({ tilt: 0, pan: 0 });

  const handleCtrl = (direction) => () => {
    let t = tilt;
    let p = pan;
    let head = 'head_tilt';
    switch (direction) {
      case 'up':
        t -= t > TILT.min ? 5 : 0;
        break;
      case 'left':
        head = 'head_pan';
        p += p < PAN.max ? 10 : 0;
        break;
      case 'right':
        head = 'head_pan';
        p -= p > PAN.min ? 10 : 0;
        break;
      default:
        t += t < TILT.max ? 5 : 0;
        break;
    }

    setCam({ tilt: t, pan: p });
    simulatePibo(head, head === 'head_tilt' ? t : p);
  };

  useEffect(() => {
    publishToRoom(
      janus,
      opaqueId,
      caller,
      true,
      (_sfutest, eventType, data) => {
        if (_sfutest) {
          setSfuTest(_sfutest);
        }
        if (eventType === 'registered') {
          setPlayerState('Ready');
        } else if (eventType === 'calling') {
          setRemotePlayerState('Calling');
        } else if (eventType === 'onlocalstream') {
          mystream = data;
          const videoContainer = videoArea.current;
          const videoPlayer = videoContainer.querySelector(
            '.janus-video-player',
          );

          Janus.attachMediaStream(videoPlayer, mystream);
          if (
            _sfutest.webrtcStuff.pc.iceConnectionState !== 'completed' &&
            _sfutest.webrtcStuff.pc.iceConnectionState !== 'connected'
          ) {
            setPlayerState('Live');
          }
          const videoTracks = mystream.getVideoTracks();
          if (
            videoTracks === null ||
            videoTracks === undefined ||
            videoTracks.length === 0
          ) {
            setPlayerState('Error');
          }
        } else if (eventType === 'onremotestream') {
          remotestream = data;
          const videoContainer = remoteVideoArea.current;
          const videoPlayer = videoContainer.querySelector(
            '.janus-video-player',
          );

          Janus.attachMediaStream(videoPlayer, remotestream);
          if (
            _sfutest.webrtcStuff.pc.iceConnectionState !== 'completed' &&
            _sfutest.webrtcStuff.pc.iceConnectionState !== 'connected'
          ) {
            setRemotePlayerState('Live');
          }
          const videoTracks = remotestream.getVideoTracks();
          if (
            videoTracks === null ||
            videoTracks === undefined ||
            videoTracks.length === 0
          ) {
            setRemotePlayerState('Error');
          }
        } else if (eventType === 'oncleanup') {
          onStopClick(_sfutest);
        } else if (eventType === 'hangup') {
          onStopClick(_sfutest);
        } else if (eventType === 'on') {
          setPlayerState('Live');
          setRemotePlayerState('Live');
        } else if (eventType === 'down') {
          setPlayerState(null);
          setRemotePlayerState(null);
        } else if (eventType === 'error') {
          if (data.error.indexOf('already taken') < 0) {
            setPlayerState('Error');
            setRemotePlayerState('Error');
            setIsMuted(false);
          }
        }
      },
    );
  }, [janus]);

  useMount(() => {
    callPibo(true);
  });

  useUnmount(() => {
    janus.destroy();
    callPibo(false);
  });

  const playerElement = children || <JanusPlayer />;

  return (
    <Grid container spacing={1}>
      <Grid item xs={12} md={8}>
        <Typography variant="h6" gutterBottom component="div">
          {`${callee}와 전화 연결`}
        </Typography>
      </Grid>
      <Grid item xs={12} md={4} sx={{ display: 'flex', justifyContent: 'end' }}>
        {playerState && playerState !== 'Live' && (
          <GreenButton size="large" onClick={onStartClick}>
            <CallIcon />
          </GreenButton>
        )}
        {playerState === 'Live' && (
          <>
            {isMuted && (
              <IconButton
                color="secondary"
                onClick={onUnMuteClick}
                aria-label="unmute"
              >
                <VolumeUpIcon />
              </IconButton>
            )}
            {!isMuted && (
              <IconButton
                color="secondary"
                onClick={onMuteClick}
                aria-label="mute"
              >
                <VolumeOffIcon />
              </IconButton>
            )}
          </>
        )}
        {playerState === 'Live' && (
          <RedButton size="large" onClick={onStopClick}>
            <CallEndIcon />
          </RedButton>
        )}
      </Grid>
      <Grid item xs={12} md={6}>
        <div style={{ width: '100%', height: 'auto' }}>
          {React.cloneElement(playerElement, {
            ref: videoArea,
            isPublisher: true,
            status: playerState,
            isMuted,
          })}
        </div>
      </Grid>
      <Grid item xs={12} md={6}>
        <div className="janus-subscriber">
          <div style={{ width: '100%', height: 'auto' }}>
            {React.cloneElement(playerElement, {
              ref: remoteVideoArea,
              isPublisher: false,
              status: remotePlayerState,
            })}
          </div>
        </div>
        <div>
          <Stack direction="row" spacing={2} alignItems="center">
            <Typography variant="subtitle2">카메라 조정</Typography>
            <Button
              variant="contained"
              startIcon={
                <ArrowBackIosIcon
                  sx={{ transform: 'rotate(90deg) translateX(5px)' }}
                />
              }
              onClick={handleCtrl('up')}
              disabled={tilt <= TILT.min}
            >
              위
            </Button>
            <Button
              variant="contained"
              startIcon={
                <ArrowForwardIosIcon
                  sx={{ transform: 'rotate(90deg) translateY(5px)' }}
                />
              }
              onClick={handleCtrl('down')}
              disabled={tilt >= TILT.max}
            >
              아래
            </Button>
            <Button
              variant="contained"
              startIcon={<ArrowBackIosIcon />}
              onClick={handleCtrl('left')}
              disabled={pan >= PAN.max}
            >
              왼쪽
            </Button>
            <Button
              variant="contained"
              startIcon={<ArrowForwardIosIcon />}
              onClick={handleCtrl('right')}
              disabled={pan <= PAN.min}
            >
              오른쪽
            </Button>
          </Stack>
        </div>
      </Grid>
    </Grid>
  );
};

export default JanusPublisher;
